import { useEffect, useState } from "react";

import { uploadsApi, ApiError, type Upload, type Trade } from "../lib/api";

/**
 * Drill-down view for a single upload. Shows status, narrative, deterministic
 * stats, and (if parsed) the individual trades.
 */
export default function UploadDetailView({
  upload_id,
  onClose,
}: {
  upload_id: string;
  onClose: () => void;
}) {
  const [upload, setUpload] = useState<Upload | null>(null);
  const [trades, setTrades] = useState<Trade[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const u = await uploadsApi.get(upload_id);
        if (cancelled) return;
        setUpload(u);
        if (u.status === "parsed") {
          const { items } = await uploadsApi.trades(upload_id);
          if (!cancelled) setTrades(items);
        }
      } catch (e) {
        if (!cancelled) setError(messageFrom(e));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [upload_id]);

  if (error) {
    return (
      <section className="upload-detail">
        <div className="error">{error}</div>
        <button onClick={onClose}>Back</button>
      </section>
    );
  }

  if (!upload) {
    return (
      <section className="upload-detail">
        <p>Loading…</p>
      </section>
    );
  }

  return (
    <section className="upload-detail">
      <header className="accounts-header">
        <div>
          <h1>{upload.filename}</h1>
          <div className="muted">
            {upload.firm_name} • {new Date(upload.uploaded_at).toLocaleString()} •{" "}
            <StatusBadge status={upload.status} />
          </div>
        </div>
        <button className="link-button" onClick={onClose}>
          Back to list
        </button>
      </header>

      {upload.status === "parse_failed" && (
        <div className="error">
          <strong>Parse failed.</strong> {upload.error}
        </div>
      )}
      {upload.status === "no_parser" && (
        <div className="error">
          <strong>No parser available for {upload.firm_name}.</strong> The file is preserved in S3.
          Write a parser and re-trigger.
        </div>
      )}
      {(upload.status === "pending" || upload.status === "parsing") && (
        <p className="muted">Still {upload.status}… the list view will auto-refresh.</p>
      )}

      {upload.narrative && (
        <section className="narrative">
          <h2>Summary</h2>
          <p>{upload.narrative}</p>
        </section>
      )}

      {upload.stats && <StatsCards stats={upload.stats} />}

      {upload.status === "parsed" && trades && (
        <section>
          <h2>Trades ({trades.length})</h2>
          {trades.length === 0 ? (
            <p className="muted">No trades parsed.</p>
          ) : (
            <table className="accounts-table">
              <thead>
                <tr>
                  <th>Opened</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Qty</th>
                  <th>Open</th>
                  <th>Close</th>
                  <th>P&L</th>
                  <th>Strategy</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <tr key={t.trade_id}>
                    <td className="mono">{t.opened_at.replace("T", " ").slice(0, 19)}</td>
                    <td>{t.symbol}</td>
                    <td>{t.side}</td>
                    <td className="num">{t.quantity}</td>
                    <td className="num">{t.open_price}</td>
                    <td className="num">{t.close_price ?? "—"}</td>
                    <td className={"num " + (t.pnl >= 0 ? "pnl-pos" : "pnl-neg")}>
                      {t.pnl >= 0 ? "+" : ""}
                      {t.pnl.toFixed(2)}
                    </td>
                    <td className="mono">{t.strategy_id ?? <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </section>
  );
}

function StatsCards({ stats }: { stats: NonNullable<Upload["stats"]> }) {
  const top = [
    { label: "Trades", value: stats.trade_count.toString() },
    {
      label: "Win rate",
      value: `${(stats.win_rate * 100).toFixed(1)}%`,
    },
    {
      label: "Gross P&L",
      value: `${stats.gross_pnl >= 0 ? "+" : ""}$${stats.gross_pnl.toFixed(2)}`,
      tone: stats.gross_pnl >= 0 ? "pos" : "neg",
    },
    { label: "Max DD", value: `$${stats.max_drawdown.toFixed(2)}`, tone: "neg" as const },
  ];

  return (
    <section className="stats-grid">
      {top.map((s) => (
        <div key={s.label} className={"stat-card " + (s.tone ? `stat-${s.tone}` : "")}>
          <div className="stat-label">{s.label}</div>
          <div className="stat-value">{s.value}</div>
        </div>
      ))}
    </section>
  );
}

function StatusBadge({ status }: { status: Upload["status"] }) {
  const cls = `status-badge status-${status}`;
  return <span className={cls}>{status.replace("_", " ")}</span>;
}

function messageFrom(e: unknown): string {
  if (e instanceof ApiError) return `${e.message} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return String(e);
}
